import type {
	AnchorHTMLAttributes,
	KeyboardEvent,
	KeyboardEventHandler,
	MouseEventHandler,
	Ref,
} from 'react';

import Icon from 'components/Icon';
import { ButtonLink } from 'components/Link';
import type { MainLinkFields } from 'models/sitecore';
import type { ColorString } from 'types';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';

export type DropdownLinkClickHandler = (linkId: string) => void;

export type LinkListItemTabHandler = (
	linkId: string,
	level: number,
	event: KeyboardEvent<HTMLAnchorElement>,
) => void;

interface Props {
	baseId: string;
	isSelected?: boolean;
	level: number;
	link: MainLinkFields;
	linkBarColor?: ColorString;
	onClick?: DropdownLinkClickHandler;
	onTab?: LinkListItemTabHandler;
	ref?: Ref<HTMLAnchorElement>;
}

/** A single link in the dropdown tree */
export default function PageHeaderLinkListItem({
	baseId,
	isSelected = false,
	level,
	link,
	linkBarColor,
	onClick,
	onTab,
	ref,
}: Props) {
	const handleClick: MouseEventHandler<HTMLAnchorElement> = () => {
		onClick?.(link.id);
	};

	const handleKeydown: KeyboardEventHandler<HTMLAnchorElement> = (e) => {
		if (e.key === 'Tab') {
			onTab?.(link.id, level, e);
		}
	};

	let linkProps: AnchorHTMLAttributes<HTMLAnchorElement> = {};
	if (onClick) {
		linkProps = {
			'onClick': handleClick,
			'aria-controls': `${baseId}-${level + 1}`,
			'aria-expanded': isSelected,
		};
	}
	if (onTab) {
		linkProps.onKeyDown = handleKeydown;
	}

	const isTopLevel = level === 1;
	const isNestedLevel = level === 2 || level === 3;

	return (
		<ButtonLink
			ref={ref}
			href={link.href}
			className={cn(
				'flex items-center py-3 pr-4 pl-8 text-md hover:underline md:px-6',
				isTopLevel &&
					'font-bold md:py-3 md:text-lg md:hover:bg-base md:hover:no-underline forced-colors:hover:underline',
				isTopLevel && isSelected && 'bg-base forced-colors:underline',
				isNestedLevel && 'md:py-2',
				isNestedLevel && isSelected && 'font-bold',
			)}
			{...linkProps}
		>
			{(linkBarColor || link.color) && (
				<div
					className={cn(
						'mr-4 min-h-[24px] w-1 shrink-0 self-stretch rounded-2xs forced-color-adjust-none',
						isTopLevel && 'md:min-h-[32px]',
					)}
					style={{ backgroundColor: linkBarColor || link.color }}
				/>
			)}
			{link.text}
			{Boolean(is.arrayWithLength(link.links) && onClick) && (
				<Icon name="chevron-right" className="ml-auto" />
			)}
		</ButtonLink>
	);
}
PageHeaderLinkListItem.displayName = 'PageHeaderLinkListItem';
